import React, {useMemo, useState} from 'react';
import {SelectionMode} from 'components/FluentUICompat';
import {useTranslation} from 'react-i18next';
import {
	funcDecodeHex24ToDocId,
	InfinityList,
	InfinityListItemWithId,
} from 'components/InfinityList';
import {RegDocFilterOptions, RegulatoryDocument} from 'types';
import {useNavigate} from 'react-router-dom';
import {
	useGetVoProRegDocNextLazyQuery,
	useGetVoProRegDocQueryLazyQuery,
	useGetVoProRegDocSortOrderLazyQuery,
	useGetVoProSummaryLazyQuery,
} from './hooks/RegulatoryDocuments.generated';
import {getHistoryEntryStringHtml as getHistoryEntryHtmlString} from 'components/DetailsSection/HistoryField';
import {
	useReportsPageColumn,
	useReportsPageColumnMetaData,
	useReportsPageFilterOptions,
} from './ReportsPageColumns';
import {useReportsPageViews} from './ReportsPageViews';
import {useInfinityListStandardHooks} from 'components/InfinityList/InfinitListStandardHooks';
import {handleClickVoProPage} from 'components/InfinityList/Excel/XLVoPro';
import {TFunction} from 'i18next';
import {useGetAllKeywordsQuery} from 'features/AdminSection/hooks/keywords.generated';
import {useGetAllMarketsQuery} from 'features/AdminSection/hooks/markets.generated';
import {useUserContext} from 'authentication/UserContext';

const initialSortField = 'workflow.clearingTimestamp';
const initialSortOrder = 'DESC';
const intBatchSize = 50;

// *page object ----------------------------------------------------------------------------------------------------------------------
export const ReportsPage = () => {
	const {t: tPage} = useTranslation('features/reports', {
		keyPrefix: 'ReportsPage',
	});

	const {t: tList} = useTranslation('features/reports', {
		keyPrefix: 'ReportsList',
	});

	const {t: tExcel} = useTranslation();

	const {isAdmin} = useUserContext();

	const [dsSummary, setDSSummary] = useState<DataStoreRegulatoryDocument>({});

	const [hoveredRegulatoryDocument, setHoveredRegulatoryDocument] =
		useState<RegulatoryDocument>();

	const {data: keywordData} = useGetAllKeywordsQuery();
	const {data: marketData} = useGetAllMarketsQuery();

	const [getSortOrder] = useGetVoProRegDocSortOrderLazyQuery();

	const [getVoProRegDocQuery, {loading: loadingQuery, data: dataQuery}] =
		useGetVoProRegDocQueryLazyQuery();

	const [getVoProRegDocsNext, {loading: loadingDocuments}] =
		useGetVoProRegDocNextLazyQuery();

	const [getVoProSummary, {loading: loadingSummary}] =
		useGetVoProSummaryLazyQuery();

	const navigate = useNavigate();

	const views = useReportsPageViews();

	const initialView = 'STD';

	const fetchSummaryData = async (arrIdsNext: string[]) => {
		const arrIdsResult = arrIdsNext.map(x => funcDecodeHex24ToDocId(x));
		const preFilteredIds = arrIdsResult.filter(x => dsSummary[x] === undefined);

		if (preFilteredIds.length > 0) {
			const dataSummaryNext: DataStoreRegulatoryDocument = {};
			const {data: dataSummary} = await getVoProSummary({
				variables: {
					nextArgs: {preFilteredIds},
				},
			});

			(dataSummary?.voProRegDocNext ?? []).forEach(x => {
				dataSummaryNext[funcDecodeHex24ToDocId(x.id)] = x as RegulatoryDocument;
			});

			const dsSummaryNew = {
				...dsSummary,
				...dataSummaryNext,
			};

			const arrRegulatoryDocumentNext = arrIdsResult
				.map(x => dsSummaryNew[x])
				.filter(x => x !== undefined);

			setDSSummary(dsSummaryNew);

			return arrRegulatoryDocumentNext as RegulatoryDocument[];
		}

		const arrRegulatoryDocumentNext = arrIdsResult
			.map(x => dsSummary[x])
			.filter(x => x !== undefined);

		return arrRegulatoryDocumentNext as RegulatoryDocument[];
	};

	const arrIds = useMemo(
		() => dataQuery?.voProRegDocQuery.regDocIds ?? [],
		[dataQuery],
	);

	const columns = useReportsPageColumn(
		hoveredRegulatoryDocument,
		setHoveredRegulatoryDocument,
		fetchSummaryData,
		loadingSummary,
	);
	const columnMetaData = useReportsPageColumnMetaData();
	const columnFilterOptions = useReportsPageFilterOptions(
		dataQuery?.voProRegDocQuery.regDocFilterOptions as
			| RegDocFilterOptions
			| undefined,
		keywordData,
		marketData,
	);

	const queryDocumentsDB = async (
		query: string,
		sortField: string,
		sortOrder: string,
	) => {
		const {data: dataQuery} = await getVoProRegDocQuery({
			variables: {
				queryArgs: {query, sortField, sortOrder},
				isAdmin,
			},
		});
		const arrDocumentNext = (dataQuery?.voProRegDocQuery.regDocsBatch ??
			[]) as RegulatoryDocument[];
		const arrIDsNew = dataQuery?.voProRegDocQuery.regDocIds ?? [];
		return {arrDocumentNext, arrIDsNew};
	};
	const getDocumentsNextDB = async (preFilteredIds: string[]) => {
		const {data: dataDocumentsNext} = await getVoProRegDocsNext({
			variables: {nextArgs: {preFilteredIds}, isAdmin},
		});

		return (dataDocumentsNext?.voProRegDocNext ?? []) as RegulatoryDocument[];
	};

	const getSortOrderDB = async (
		preFilteredIds: string[],
		sortField: string,
	) => {
		const {data: dataSortOrder} = await getSortOrder({
			variables: {
				sortArgs: {sortField, preFilteredIds},
			},
		});

		return dataSortOrder?.voProRegDocSortOrder ?? preFilteredIds;
	};

	const handleExcelExport = async (arrDocuments: RegulatoryDocument[]) => {
		if (arrDocuments.length > 0) {
			const arrDocumentsSummary = await fetchSummaryData(
				arrDocuments.map(x => x.id),
			);
			const arrDocumentsXL = arrDocuments.map((x, i) => {
				return {
					...x,
					summary: getVoProSummaryHTML(
						arrDocumentsSummary.at(i) as RegulatoryDocument | undefined,
						tList,
					),
				};
			});

			await handleClickVoProPage(
				arrDocumentsXL,
				columns.filter(x => x.hiddenAlways === undefined).map(x => x.name),
				tExcel,
			);
		}
	};

	const {
		arrDocument,
		keyPrefix,
		linksFilters,
		firstRenderSTD,
		applyFilterSTD,
		applySortSTD,
		loadItemsNextSTD,
		excelExportSTD,
	} = useInfinityListStandardHooks({
		views,
		intBatchSize,
		initialSortField,
		initialSortOrder,
		initialView,
		arrIds,
		columns,
		columnMetaData,
		queryDocumentsDB,
		getDocumentsNextDB,
		getSortOrderDB,
		handleExcelExport,
	});

	const handleViewingItem = (item: RegulatoryDocument) => {
		navigate({
			pathname: `/${'regulatoryDocuments'}/${
				(item as InfinityListItemWithId).id
			}`,
		});
	};

	const createHref = (item: unknown) =>
		`/${'regulatoryDocuments'}/${(item as InfinityListItemWithId).id}`;

	const numberOfElements = arrIds.length ?? 1;

	// *react object ------------------------------------------------------------------------------------------------------------------
	return (
		<>
			<p style={{marginTop: 10, paddingLeft: 10}}>
				{tPage('ReportsSubHeader')}
			</p>
			<InfinityList
				pageName={tPage('Reports')}
				items={arrDocument}
				sticky={true}
				key={`reports-${isAdmin}`}
				columns={columns}
				columnMetaData={columnMetaData}
				columnFilterOptions={columnFilterOptions}
				selectionMode={SelectionMode.none}
				stillLoading={loadingQuery || loadingDocuments || loadingSummary}
				initalFilters={linksFilters}
				initialSortField={initialSortField}
				initialSortOrder={initialSortOrder}
				initialView={initialView}
				keyPrefix={keyPrefix}
				firstRender={firstRenderSTD}
				loadItemsNext={loadItemsNextSTD}
				applyFilter={applyFilterSTD}
				applySort={applySortSTD}
				excelExport={excelExportSTD}
				handleInvokeItem={handleViewingItem}
				createHref={createHref}
				numberOfElements={numberOfElements}
				views={views}
			/>
		</>
	);
};

export interface DataStoreRegulatoryDocument {
	[key: string]: RegulatoryDocument;
}

const getVoProSummaryHTML = (
	regdoc: RegulatoryDocument | undefined,
	tList: TFunction,
): string => {
	const {historyHtmlString, reportSummaryString} = getVoProSummary(
		regdoc,
		tList,
	);

	const summaryPart1 =
		historyHtmlString === '<p></p>'
			? `<b>${tList('ChangeHistory')}</b>:<p></p>` + historyHtmlString
			: '';

	const summaryPart2 = `<b>${tList(
		'Summary',
	)}</b>:<p></p>${reportSummaryString}`;

	return summaryPart1 + summaryPart2;
};

export const getVoProSummary = (
	regdoc: RegulatoryDocument | undefined,
	tList: TFunction,
) => {
	const summaryDefault = `<p>${tList('EmptySummary')}</p>`;

	const historyHtmlArray =
		regdoc?.changeHistoryAuditLogs
			.filter(log => log.change.value)
			.sort((log1, log2) => (log1.createdAt > log2.createdAt ? 1 : -1))
			.slice(0, 20)
			?.map((hitem: any) => {
				const historyHtml = getHistoryEntryHtmlString(
					new Date(hitem.createdAt),
					hitem.change.value,
				);
				return historyHtml;
			}) ?? [];

	const historyHtmlString = `<p>${historyHtmlArray.join('</p><p>')}</p>`;

	const reportSummaryString = regdoc?.summary ?? summaryDefault;

	return {historyHtmlString, reportSummaryString};
};
